import {defineStore} from "pinia";
import {ErrorMessageBag} from "@/stores/errors/ErrorMessageBag.ts";
import {User} from "@/models/User.model";
import {orgApi, api, setOrganisationId} from "@/utils/Api.util";
import {useClassroomsStore} from "@/stores/Classrooms.store";
import {ApiErrorMessageBag} from "@/stores/errors/ApiErrorMessageBag.ts";
import config from "@/config/app.config";
import { OrderDirection, OrderParameter } from "@/models/OrderParameter.model";
import { useDefaultOrderStore } from "@/stores/DefaultOrder.store";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import { UserRole } from "@/models/UserRole";
import { Organisation } from "@/models/Organisation.model";

interface UserStoreState {
	errors: ErrorMessageBag | null,
	me: User | null,
	users: User[],
	roles: string[],
	order: OrderParameter,
	loading: boolean,
	loadedOnce: boolean,
	_loadOncePromise: Promise<void> | null
}

export const useUsersStore = defineStore('users', {

	state: (): UserStoreState => ({
		errors: null,
		me: null,
		users: [],
		roles: [ 'owner', 'admin', 'teacher' ],
		order: useDefaultOrderStore().usersOrder,
		loading: false,
		loadedOnce:	false,
		_loadOncePromise: null,
	}),

	getters: {
		admins: (state) => {
			return state.users.filter(u => u.role === 'admin' || u.role === 'owner');
		},

		owners: (state) => {
			return state.users.filter(u => u.role === 'owner');
		},

		teachers: (state) => {
			return state.users.filter(u => u.role === 'teacher');
		},

		fromIds: (state) => (ids: string[]) => {
			return state.users.filter(u => u.id && ids.indexOf(u.id) >= 0);
		}
	},

	actions: {

		new() {
			let newUser = new User();

			const organisation = useOrganisationsStore().currentOrganisation;
			if (!organisation) {
				throw new Error('No organisation set');
			}

			const userRole = new UserRole(useOrganisationsStore().getCurrentOrganisation(), 'teacher', true);
			newUser.roles.push(userRole);

			return newUser;
		},

		clearErrors() {
			this.errors = null;
		},

		async loadOnce() {
			if (this.loadedOnce) {
				return;
			}

			if (this._loadOncePromise) {
				return this._loadOncePromise;
			}

			this._loadOncePromise = (async () => {
				await this.load();
			})();

			try {
				await this._loadOncePromise;
			} finally {
				this._loadOncePromise = null;
			}
		},

		async load() {

			this.loading = true;
			let response;

			try {
				response = await orgApi.get("users", {
					params: {
						mask: [
							'*',
							'roles.*',
						].join(',')
					}
				});

				this.loadedOnce = true;
			} catch (e) {
				throw ApiErrorMessageBag.fromAxiosException(e);
			} finally {
				this.loading = false;
			}

			this.users = response.data.data.map(User.mapFromServer);

			this.orderBy(this.order);
		},

		changeOrder(order: OrderParameter) {
			this.order = order;
			this.orderBy(this.order);
			useDefaultOrderStore().setDefaultUsersOrder(order);
		},

		async loadFromEmail(email: string) {

			if (!email) {
				return null;
			}

			const response = await api.get("users", {
				params: {
					email
				}
			});

			const users = response.data.data.map(User.mapFromServer);
			return users.length > 0 ? users[0] : null;

		},

		/**
		 * Warning: should only be called once during an app loading phase.
		 * Do not call this method within component initialisation, as the access tokens might not be set yet.
		 */
		async loadMe(forceReload: boolean = false): Promise<User> {

			// already loaded?
			if (this.me && !forceReload) {
				return this.me;
			}

			let response;
			try {
				response = await api.get("users/me", {
					params: {
						mask: [
							'*',
							'roles.*',
							'roles.organisation.*',
							'roles.organisation.features.*',
							'roles.organisation.classrooms.*',
							'roles.organisation.agenda_settings.*',
							'roles.organisation.classrooms.goalCollections.*',
						].join(',')
					}
				});
			} catch (e) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}

			this.me = User.mapFromServer(response.data.data);
			if (this.me.roles.length === 0) {
				return this.me;
			}

			// Check if config organisation is set correctly
			const activeOrganisations = this.me.roles
				.filter(o => o.active && o.organisation.active)
				.map(o => o.organisation);

			let currentOrganisationIndex = activeOrganisations.findIndex(o => o.id == config.ORG_ID);

			let currentOrganisation: Organisation;

			// Organisation not found?
			if (currentOrganisationIndex < 0) {
				if (activeOrganisations.length === 1) {
					currentOrganisation = activeOrganisations[0];
					if (currentOrganisation.id) {
						config.ORG_ID = currentOrganisation.id;
					}
				} else {
					// No organisation selected yet, redirect to organisation selection
					return this.me;
				}
			} else {
				currentOrganisation = activeOrganisations[currentOrganisationIndex];
			}

			if (!currentOrganisation.id) {
				throw new Error('No organisation id set');
			}

			setOrganisationId(currentOrganisation.id);

			// Automatically select first classroom
			useClassroomsStore().setDefaultCurrentClassroom(this.me, currentOrganisation);

			return this.me;

		},

		/**
		 * Get the currently logged in user.
		 * @returns User
		 */
		getMe(): User {
			if (!this.me) {
				throw new Error('Current user not loaded');
			}
			return this.me;
		},

		clearMe() {
			this.me = null;
		},

		orderBy(orderBy: OrderParameter) {
			this.sortItemsBy(orderBy.property, orderBy.direction);
		},

		sortItemsBy(attr: any, direction = OrderDirection.ASC) {
			this.users.sort(
				(a: any, b: any) => {
					let result;

					if (!a[attr] && !b[attr]) {
						// Sort by id
						result = parseInt(a.id) - parseInt(b.id);
					} else if (!a[attr]) {
						result = -1;
					} else if (!b[attr]) {
						result = 1;
					} else {
						result = a[attr].localeCompare(b[attr]);
					}

					// Same? Sort by id.
					if (result === 0) {
						result = parseInt(a.id) - parseInt(b.id);
					}

					if (direction === OrderDirection.DESC) {
						result = -result;
					}

					return result;
				}
			);
		},

		async save(user: User, sendInvitationEmail: boolean = false) {

			this.clearErrors();

			if(user.id) {
				try {
					const response = await orgApi.put("users/" + user.id, user.getServerData());
					this.updateStoreItem(user);
				} catch (e: any) {
					this.errors = ApiErrorMessageBag.fromAxiosException(e);
					throw this.errors;
				}

			} else {
				try {
					const data = user.getServerData();
					data.send_invitation = sendInvitationEmail;

					const response = await orgApi.post("users", data);
					user.setFromServerData(response.data.data);

					this.addStoreItem(user);
				} catch (e: any) {
					this.errors = ApiErrorMessageBag.fromAxiosException(e);
					throw this.errors;
				}
			}

			this.orderBy(this.order);
		},

		async savePreferences(user: User) {
			if(!user.id) {
				throw new Error('User ID not set');
			}

			const response = await api.put("users/" + user.id + '/preferences', {
				locale: user.locale,
				publicName: user.publicName,
				publicImage: user.publicImage,
				publicDescription: user.publicDescription,
			});

			if (this.me && this.me.id === user.id) {
				// Also store the app locale
				if (user.locale) {
					localStorage.setItem('locale', user.locale);
				}
			}

			window.location.reload(); // hacky hacky joy joy
		},

		async delete(user: User) {

			this.clearErrors();

			try {
				const response = await orgApi.delete("users/" + user.id);
				this.removeStoreItem(user);
			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}

		},

		updateStoreItem(user: User) {
			const index = this.users.findIndex((u: User) => u.id === user.id);
			if (index !== -1) {
				this.users[index] = user.clone();
			}
		},

		addStoreItem(user: User) {
			this.users.push(user.clone());
			this.orderBy(this.order);
		},

		removeStoreItem(user: User) {
			const index = this.users.findIndex(v => v.id === user.id);
			if (index >= 0) {
				this.users.splice(index, 1);
			}
		},

		async impersonate(userId: string, organisationId: string | null = null) {
			const response = await api.post("users/" + userId + "/impersonate");

			localStorage.setItem('impersonating_access_token', localStorage.getItem('access_token') || '');
			localStorage.setItem('access_token', response.data.accessToken);

			if (organisationId) {
				localStorage.setItem('organisation_id', organisationId);
			} else {
				localStorage.removeItem('organisation_id');
			}

			window.location.reload();
		},

		async stopImpersonating() {
			if (!this.me) {
				return;
			}

			const response = await api.delete("users/" + this.me.id + "/impersonate");

			localStorage.setItem('access_token', localStorage.getItem('impersonating_access_token') || '');
			localStorage.removeItem('impersonating_access_token');

			window.location.reload();
		}
	}

});
